import React from 'react';

import { Button, Checkbox, Tabs } from 'components/ui';
import Modal from 'components/ui/Modal';
import { cx } from 'helpers/StylingHelpers';
import { useTranslation } from 'hooks/common/useTranslation';

import type {
  ConsentCookieType, IConsent, IConsentCookie, IModalProps
} from 'types';
import { CONSENT_COOKIE_TYPES } from 'types';

import styles from './ConsentModal.module.scss';

const TABS = [
  { label: 'COOKIES.TYPES.FUNCTIONAL', type: CONSENT_COOKIE_TYPES.FUNCTIONAL, readonly: true },
  { label: 'COOKIES.TYPES.ANALYTICS', type: CONSENT_COOKIE_TYPES.ANALYTICS },
  { label: 'COOKIES.TYPES.ADVERTISING', type: CONSENT_COOKIE_TYPES.ADVERTISING }
];

interface Props extends IModalProps {
  cookies: IConsentCookie[],
  onConfirmClick: (updatedConsent: Partial<IConsent>, onSuccess: () => void, onError?: () => void) => void
}

const ConsentModal = (props: Props) => {

  const {
    isOpen,
    cookies,
    //
    onConfirmClick,
    onClose,
  } = props;

  // Hooks

  const { t } = useTranslation();

  const [draftCookies, setDraftCookies] = React.useState<IConsentCookie[]>();
  const [isLoading, setIsLoading] = React.useState(false);

  // Effects

  React.useEffect(() => {
    const updatedCookies = cookies?.map((cookie) => {
      const { type } = cookie || {};

      if (type === CONSENT_COOKIE_TYPES.FUNCTIONAL) {
        return {
          ...cookie,
          isAccepted: true
        };
      }

      return cookie;
    });

    setDraftCookies(updatedCookies);
  }, [cookies]);

  // Handlers

  const handleClose = () => {
    onClose();
  };

  const handleAccept = () => {
    setIsLoading(true);
    onConfirmClick(
      {
        timestamp: undefined,
        cookies: draftCookies,
      },
      () => {
        handleClose();
        setIsLoading(false);
      },
      () => {
        setIsLoading(false);
      }
    );
  };

  // Props

  const availableTabs = TABS.filter(({ type }) => hasCookieType(type, draftCookies));

  // Render

  return (
    <Modal
      isOpen={isOpen}
      //
      classNames={{
        overlay: styles.root,
        content: styles.content
      }}
      //
      title={t('COOKIES.MODAL.TITLE')}
      //
      responsiveProps={{
        isFullScreen: true,
        position: 'bottom',
      }}
      headerProps={{
        responsiveProps: {
          withBorder: true,
        }
      }}
      //
      onClose={handleClose}
      dataTestId="cookies-modal"
    >

      {/* Description */}
      <div className={styles.description}>
        {t('COOKIES.MODAL.DESCRIPTION')}
      </div>

      {/* Tabs */}
      <div className={styles.tabs}>
        <Tabs.Container defaultIndex={0}>
          <Tabs.List>
            {
              availableTabs.map(({ label }) => (
                <Tabs.Tab key={label}>
                  <div className={styles.tabTitle} data-testid="cookies-modal-tab">
                    {t(label)}
                  </div>
                </Tabs.Tab>
              ))
            }
          </Tabs.List>
          {
            availableTabs.map(({ label, type, readonly }) => (
              <Tabs.Panel key={label}>
                <CookiesPanel
                  type={type}
                  readonly={readonly}
                  cookies={draftCookies || []}
                  setCookies={setDraftCookies}
                />
              </Tabs.Panel>
            ))
          }
        </Tabs.Container>
      </div>

      {/* Actions */}
      <div className={styles.actionsContainer}>
        <Button
          className={styles.acceptButton}
          //
          variant="primary"
          loading={isLoading}
          disabled={isLoading}
          //
          onClick={handleAccept}
          //
          data-testid="cookies-modal-accept"
        >
          {t('ACTIONS.ACCEPT')}
        </Button>
      </div>

    </Modal>
  );
};

// Components

interface CookiesPanelProps {
  type: ConsentCookieType,
  cookies: IConsentCookie[],
  readonly?: boolean,
  setCookies: (cookies: IConsentCookie[]) => void
}

const CookiesPanel = (props: CookiesPanelProps) => {
  const {
    type,
    cookies,
    readonly,
    setCookies
  } = props;

  // Hooks

  const { t } = useTranslation();

  // Props

  const displayCookies = cookies.filter((cookie) => cookie.type === type);
  const allActive = displayCookies.every((cookie) => cookie?.isAccepted);
  const allInactive = displayCookies.every((cookie) => !cookie.isAccepted);

  // Handlers

  const selectAll = () => {
    setCookies(cookies.map((cookie) => (
      cookie.type === type
        ? { ...cookie, isAccepted: true }
        : cookie
    )));
  };

  const deselectAll = () => {
    setCookies(cookies.map((cookie) => (
      cookie.type === type
        ? { ...cookie, isAccepted: false }
        : cookie
    )));
  };

  const onCheckboxClick = (id: number) => {
    setCookies(cookies.map((cookie) => (
      cookie.id === id
        ? ({ ...cookie, isAccepted: !cookie.isAccepted })
        : cookie
    )));
  };

  // Render

  return (
    <div className={styles.tabPanel}>

      {/* Select All */}
      {
        !readonly
          && (
            <div className={styles.selectAllContainer}>
              <div className={styles.selectAllLabel}>{t('COOKIES.MODAL.SELECT_ALL')}</div>
              <div className={styles.selectAllToggle}>
                <button
                  type="button"
                  className={cx(styles.yesBtn, { [styles.active]: allActive })}
                  onClick={selectAll}
                >
                  {t('ACTIONS.YES')}
                </button>
                <button
                  type="button"
                  className={cx(styles.noBtn, { [styles.active]: allInactive })}
                  onClick={deselectAll}
                >
                  {t('ACTIONS.NO')}
                </button>
              </div>
            </div>
          )
      }

      {/* Cookie List */}
      {
        displayCookies.map((cookie) => {
          const {
            isAccepted, name, id
          } = cookie;
          return (
            <div key={id} className={cx(styles.cookie, { [styles.readonly]: readonly })}>
              <Checkbox
                labelClassName={styles.cookieLabel}
                label={name}
                checked={isAccepted}
                disabled={!!readonly}
                readOnly={readonly}
                onChange={() => onCheckboxClick(id)}
                data-testid="cookies-modal-checkbox"
              />
            </div>
          );
        })
      }

    </div>
  );
};

// Helpers

const hasCookieType = (type: ConsentCookieType, cookies?: IConsentCookie[]) => {
  if (!cookies) return false;
  return cookies.some(({ type: cookieType }) => type === cookieType);
};

// Export

export default ConsentModal;
